import { StandardEvents } from '@shopify/events';
import { lockScroll, unlockScroll } from '@theme/utilities';

/*
 * Gustave product page behaviour for sections/gs-product.liquid.
 *
 * The purchase itself is Horizon's <product-form-component>: it posts the form, dispatches the
 * cart-lines-update standard event (which updates the header count and re-renders the cart drawer)
 * and shows errors. This element adds what the Gustave layout needs around it:
 * - size radios → hidden variant id, ?variant= in the URL, price, same-size links to other colours
 *   (products without a size option, e.g. bags, submit the enabled hidden variant id as is)
 * - "Select a size" state, and a reminder when "Add to bag" is pressed without a size
 * - opening Horizon's cart drawer after a successful add (or going to /cart when there is no drawer)
 * - the mobile gallery counter, a full-screen lightbox, the sticky mobile bar and the sticky panel offset
 */

const DESKTOP_QUERY = window.matchMedia('(min-width: 990px)');
const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');
const ADDED_STATE_DURATION = 2600;
const PANEL_GAP = 32;

/**
 * @typedef {{ id: number, available: boolean, price: string, compare: string | null }} GsVariant
 */

/** @returns {ScrollBehavior} */
function scrollBehavior() {
  return REDUCED_MOTION_QUERY.matches ? 'auto' : 'smooth';
}

/**
 * Index of the slide closest to the start of a horizontal scroller (LTR and RTL).
 * @param {HTMLElement} track
 * @returns {number}
 */
function currentSlideIndex(track) {
  const slides = track.children;
  const first = slides[0];
  const second = slides[1];
  if (!first || !second) return 0;

  const maxScroll = track.scrollWidth - track.clientWidth;
  const position = Math.abs(track.scrollLeft);
  if (maxScroll > 0 && position >= maxScroll - 2) return slides.length - 1;

  const step = Math.abs(second.getBoundingClientRect().left - first.getBoundingClientRect().left) || track.clientWidth;
  if (!step) return 0;

  return Math.min(slides.length - 1, Math.max(0, Math.round(position / step)));
}

/**
 * Scrolls a horizontal track so that `slide` sits at its start edge, without moving the page.
 * @param {HTMLElement} track
 * @param {Element} slide
 * @param {ScrollBehavior} behavior
 */
function scrollTrackTo(track, slide, behavior) {
  const rtl = getComputedStyle(track).direction === 'rtl';
  const trackRect = track.getBoundingClientRect();
  const slideRect = slide.getBoundingClientRect();
  const delta = rtl ? slideRect.right - trackRect.right : slideRect.left - trackRect.left;
  if (Math.abs(delta) < 1) return;
  track.scrollBy({ left: delta, behavior });
}

class GsProductPage extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {IntersectionObserver | null} */
  #barObserver = null;

  /** @type {ResizeObserver | null} */
  #panelObserver = null;

  /** @type {Map<string, GsVariant>} */
  #variants = new Map();

  /** The variant id the shopper has chosen ('' while no size is chosen). */
  #selectedId = '';

  #busy = false;
  #added = false;

  /** @type {number | undefined} */
  #addedTimer;

  #galleryFrame = 0;
  #lightboxFrame = 0;
  #pastButton = false;
  #endReached = false;

  /** @type {HTMLElement | null} */
  #lightboxOpener = null;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#variants = this.#readVariants();

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('pointerover', this.#onSwatchEnter, { signal });
    this.addEventListener('pointerout', this.#onSwatchLeave, { signal });
    this.addEventListener('focusin', this.#onSwatchEnter, { signal });
    this.addEventListener('focusout', this.#onSwatchLeave, { signal });
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, { signal });
    window.addEventListener('pageshow', this.#syncFromForm, { signal });

    this.#setupGallery(signal);
    this.#setupLightbox(signal);
    this.#setupStickyBar();
    this.#setupStickyPanel(signal);

    this.#syncFromForm();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#barObserver?.disconnect();
    this.#barObserver = null;
    this.#panelObserver?.disconnect();
    this.#panelObserver = null;
    clearTimeout(this.#addedTimer);
    cancelAnimationFrame(this.#galleryFrame);
    cancelAnimationFrame(this.#lightboxFrame);

    const dialog = this.#lightbox;
    if (dialog) unlockScroll(dialog);
  }

  /* ---------------------------------------------------------------- State */

  /** @returns {Map<string, GsVariant>} */
  #readVariants() {
    /** @type {Map<string, GsVariant>} */
    const variants = new Map();
    const script = this.querySelector('script[data-gs-variants]');
    if (!script?.textContent) return variants;

    try {
      const list = JSON.parse(script.textContent);
      if (Array.isArray(list)) {
        for (const variant of list) variants.set(String(variant.id), variant);
      }
    } catch (error) {
      console.warn('[gs-product] Could not read variant data', error);
    }

    return variants;
  }

  /** @returns {HTMLInputElement[]} */
  get #sizeInputs() {
    return /** @type {HTMLInputElement[]} */ (Array.from(this.querySelectorAll('input[data-gs-size]')));
  }

  /** @returns {HTMLInputElement | null} */
  get #hiddenId() {
    const input = this.querySelector('input[data-gs-variant-id]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @returns {HTMLDialogElement | null} */
  get #lightbox() {
    const dialog = this.querySelector('dialog[data-gs-lightbox]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  /**
   * Reads the current selection from the DOM: on load, after a bfcache restore (the browser may
   * restore a checked radio) and when a radio was clicked before this module ran.
   */
  #syncFromForm = () => {
    const inputs = this.#sizeInputs;
    const checked = inputs.find((input) => input.checked && !input.disabled);

    if (checked) {
      this.#select(checked, false);
      return;
    }

    this.#selectedId = inputs.length ? '' : this.#hiddenId?.value ?? '';
    this.#render();
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-gs-size]') || !input.checked) return;
    this.#select(input, true);
  };

  /**
   * @param {HTMLInputElement} input - The checked size radio.
   * @param {boolean} updateUrl - Whether to write ?variant= to the address bar.
   */
  #select(input, updateUrl) {
    this.#selectedId = input.value;

    const hidden = this.#hiddenId;
    if (hidden) hidden.value = input.value;

    const value = this.querySelector('[data-gs-size-value]');
    if (value) value.textContent = input.dataset.label ?? '';
    this.querySelector('[data-gs-size-sep]')?.removeAttribute('hidden');

    const variant = this.#variants.get(input.value);
    if (variant) this.#renderPrice(variant);

    this.#clearPrompt();
    this.#syncSiblingLinks(input.dataset.key ?? '');
    if (updateUrl) this.#updateUrl(input.value);
    this.#render();
  }

  /** @param {string} variantId */
  #updateUrl(variantId) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      // Keep history.state: Horizon stores its scroll restoration data there.
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (error) {
      // replaceState can throw in sandboxed previews; the selection still works.
    }
  }

  /** Updates the label and state of every "Add to bag" button. */
  #render() {
    const soldOut = this.dataset.available === 'false';
    const variant = this.#variants.get(this.#selectedId);
    const labels = this.dataset;

    let label = labels.labelAdd ?? '';
    let ariaDisabled = false;

    if (soldOut) {
      label = labels.labelSoldOut ?? label;
    } else if (!this.#selectedId) {
      label = labels.labelSelect ?? label;
      ariaDisabled = true;
    } else if (variant && !variant.available) {
      label = labels.labelSoldOut ?? label;
      ariaDisabled = true;
    } else if (this.#added) {
      label = labels.labelAdded ?? label;
    }

    for (const button of this.querySelectorAll('[data-gs-add]')) {
      if (!(button instanceof HTMLButtonElement)) continue;

      const text = button.querySelector('[data-gs-add-text]');
      if (text && text.textContent !== label) text.textContent = label;

      if (soldOut) {
        button.disabled = true;
      }

      if (ariaDisabled) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }

      button.toggleAttribute('data-busy', this.#busy);
      if (this.#busy) {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    }
  }

  /** @param {GsVariant} variant */
  #renderPrice(variant) {
    for (const price of this.querySelectorAll('[data-gs-price]')) {
      const current = price.querySelector('[data-gs-price-current]');
      if (current && variant.price) current.textContent = variant.price;

      const compare = price.querySelector('[data-gs-price-compare]');
      if (compare) compare.textContent = variant.compare ?? '';

      for (const element of price.querySelectorAll('[data-gs-sale-label], [data-gs-compare-wrap]')) {
        element.toggleAttribute('hidden', !variant.compare);
      }
    }
  }

  /**
   * Points the colour swatches and "Other colours" cards at the same size in each colour.
   * @param {string} sizeKey - e.g. "42eu"
   */
  #syncSiblingLinks(sizeKey) {
    /** @type {Map<string, string>} */
    const hrefs = new Map();

    for (const swatch of this.querySelectorAll('a[data-gs-swatch]')) {
      if (!(swatch instanceof HTMLAnchorElement)) continue;
      const base = swatch.dataset.gsUrl;
      if (!base) continue;

      /** @type {Record<string, number>} */
      let map = {};
      try {
        map = JSON.parse(swatch.dataset.gsVariantMap || '{}');
      } catch (error) {
        map = {};
      }

      const variantId = sizeKey ? map[sizeKey] : undefined;
      const href = variantId ? `${base}?variant=${variantId}` : base;
      hrefs.set(base, href);

      if (!swatch.hasAttribute('aria-current')) swatch.setAttribute('href', href);
    }

    for (const item of this.querySelectorAll('[data-gs-sibling]')) {
      if (!(item instanceof HTMLElement)) continue;
      const href = hrefs.get(item.dataset.gsUrl ?? '');
      if (!href) continue;
      for (const link of item.querySelectorAll('a[href]')) link.setAttribute('href', href);
    }
  }

  /* ---------------------------------------------------------------- Clicks */

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const add = target.closest('[data-gs-add]');
    if (add instanceof HTMLButtonElement) {
      if (this.#busy) {
        event.preventDefault();
        return;
      }

      if (add.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        if (!this.#selectedId) this.#promptSize();
        return;
      }

      if (add.hasAttribute('data-gs-sticky-add')) {
        event.preventDefault();
        this.#submit();
      }
      return;
    }

    const zoom = target.closest('[data-gs-zoom]');
    if (zoom instanceof HTMLElement) {
      this.#openLightbox(Number(zoom.dataset.gsZoom) || 0, zoom);
      return;
    }

    if (target.closest('[data-gs-lb-close]')) {
      this.#lightbox?.close();
      return;
    }

    if (target.closest('[data-gs-lb-prev]')) {
      this.#stepLightbox(-1);
      return;
    }

    if (target.closest('[data-gs-lb-next]')) {
      this.#stepLightbox(1);
    }
  };

  /** Submits the product form the same way the main button does (sticky bar). */
  #submit() {
    const form = this.querySelector('form[data-type="add-to-cart-form"]');
    if (!(form instanceof HTMLFormElement)) return;

    const main = this.querySelector('[data-gs-main-add]');
    const submitter = main instanceof HTMLButtonElement && main.form === form ? main : null;

    if (typeof form.requestSubmit === 'function') {
      if (submitter) {
        form.requestSubmit(submitter);
      } else {
        form.requestSubmit();
      }
    } else {
      submitter?.click();
    }
  }

  /** Brings the size grid into view and asks for a size. */
  #promptSize() {
    const fieldset = this.querySelector('[data-gs-sizes]');
    if (!(fieldset instanceof HTMLElement)) return;

    const hint = fieldset.querySelector('[data-gs-size-hint]');
    if (hint) hint.textContent = this.dataset.labelPrompt ?? '';
    fieldset.classList.add('is-prompting');

    const rect = fieldset.getBoundingClientRect();
    const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
    if (!inView) fieldset.scrollIntoView({ block: 'center', behavior: scrollBehavior() });

    const first = this.#sizeInputs.find((input) => !input.disabled);
    first?.focus({ preventScroll: true });
  }

  #clearPrompt() {
    const fieldset = this.querySelector('[data-gs-sizes]');
    fieldset?.classList.remove('is-prompting');
    const hint = fieldset?.querySelector('[data-gs-size-hint]');
    if (hint) hint.textContent = '';
  }

  /* ---------------------------------------------------------------- Swatch label */

  /** @param {Event} event */
  #onSwatchEnter = (event) => {
    const swatch = event.target instanceof Element ? event.target.closest('[data-gs-swatch]') : null;
    if (!(swatch instanceof HTMLElement)) return;
    this.#setColourName(swatch.dataset.colour ?? null);
  };

  /** @param {Event} event */
  #onSwatchLeave = (event) => {
    const swatch = event.target instanceof Element ? event.target.closest('[data-gs-swatch]') : null;
    if (!swatch) return;

    const next = /** @type {MouseEvent | FocusEvent} */ (event).relatedTarget;
    if (next instanceof Node && swatch.contains(next)) return;
    this.#setColourName(null);
  };

  /** @param {string | null} name - A colour name, or null to show the current colour again. */
  #setColourName(name) {
    const label = this.querySelector('[data-gs-colour-name]');
    if (!(label instanceof HTMLElement)) return;
    const text = name ?? label.dataset.current ?? '';
    if (label.textContent !== text) label.textContent = text;
  }

  /* ---------------------------------------------------------------- Cart */

  /**
   * Listens to the standard cart event Horizon's product form dispatches for this page's form.
   * @param {Event} event
   */
  #onCartLinesUpdate = (event) => {
    const form = this.querySelector('product-form-component');
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;

    const cartEvent = /** @type {any} */ (event);
    if (cartEvent.action && cartEvent.action !== 'add') return;

    /** @type {Promise<any> | undefined} */
    const promise = cartEvent.promise;
    if (!promise) return;

    this.#setBusy(true);

    promise
      .then((result) => {
        this.#setBusy(false);
        if (result?.detail?.didError) {
          this.#revealError();
          return;
        }
        this.#flashAdded();
        this.#openCart();
      })
      .catch((error) => {
        this.#setBusy(false);
        if (error?.name === 'AbortError') return;
        console.warn('[gs-product] Add to bag failed', error);
        this.#revealError();
      });
  };

  /**
   * Horizon writes add errors into the form's error line. When the add came from the sticky bar
   * the form is scrolled out of view, so bring the message (or the main button) back into view.
   */
  #revealError() {
    const bar = this.querySelector('[data-gs-sticky]');
    if (DESKTOP_QUERY.matches || !bar?.classList.contains('is-visible')) return;

    const error = this.querySelector('.gs-pdp__error:not(.hidden)');
    const target = error ?? this.querySelector('[data-gs-main-add]');
    target?.scrollIntoView({ block: 'center', behavior: scrollBehavior() });
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.#render();
  }

  #flashAdded() {
    this.#added = true;
    this.#render();
    clearTimeout(this.#addedTimer);
    this.#addedTimer = setTimeout(() => {
      this.#added = false;
      this.#render();
    }, ADDED_STATE_DURATION);
  }

  /** Opens Horizon's cart drawer; without a drawer (cart type "page") goes to the cart page. */
  #openCart() {
    const drawer = document.querySelector('theme-drawer#cart-drawer');

    if (!drawer) {
      window.location.assign(this.dataset.cartUrl || '/cart');
      return;
    }

    // With "Auto-open cart drawer" on, Horizon's cart-drawer-component opens it itself.
    if (drawer.querySelector('cart-drawer-component[auto-open]')) return;

    customElements.whenDefined('theme-drawer').then(() => {
      // Let the cart items re-render from the same event first, then open.
      requestAnimationFrame(() => {
        const themeDrawer = /** @type {any} */ (drawer);
        if (themeDrawer.isConnected && !themeDrawer.isOpen && typeof themeDrawer.open === 'function') {
          themeDrawer.open();
        }
      });
    });
  }

  /* ---------------------------------------------------------------- Gallery counter */

  /** @param {AbortSignal} signal */
  #setupGallery(signal) {
    const track = this.querySelector('[data-gs-track]');
    const counter = this.querySelector('[data-gs-counter]');
    if (!(track instanceof HTMLElement) || !counter) return;

    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(this.#galleryFrame);
        this.#galleryFrame = requestAnimationFrame(() => {
          const text = String(currentSlideIndex(track) + 1);
          if (counter.textContent !== text) counter.textContent = text;
        });
      },
      { passive: true, signal }
    );
  }

  /* ---------------------------------------------------------------- Lightbox */

  /** @param {AbortSignal} signal */
  #setupLightbox(signal) {
    const dialog = this.#lightbox;
    if (!dialog) return;

    dialog.addEventListener('close', this.#onLightboxClose, { signal });

    dialog.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const rtl = getComputedStyle(dialog).direction === 'rtl';
        const forward = event.key === 'ArrowRight' ? !rtl : rtl;
        this.#stepLightbox(forward ? 1 : -1);
      },
      { signal }
    );

    const track = dialog.querySelector('[data-gs-lb-track]');
    if (track instanceof HTMLElement) {
      track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(this.#lightboxFrame);
          this.#lightboxFrame = requestAnimationFrame(() => this.#updateLightboxControls());
        },
        { passive: true, signal }
      );
    }
  }

  /**
   * @param {number} index - Zero-based media index.
   * @param {HTMLElement} opener - The element to return focus to.
   */
  #openLightbox(index, opener) {
    const dialog = this.#lightbox;
    if (!dialog || dialog.open) return;

    this.#lightboxOpener = opener;
    dialog.showModal();
    lockScroll(dialog);

    const track = dialog.querySelector('[data-gs-lb-track]');
    const slide = track?.children[index];
    if (track instanceof HTMLElement && slide) scrollTrackTo(track, slide, 'auto');

    this.#updateLightboxControls();
  }

  /** @param {number} direction - 1 for next, -1 for previous. */
  #stepLightbox(direction) {
    const track = this.#lightbox?.querySelector('[data-gs-lb-track]');
    if (!(track instanceof HTMLElement)) return;

    const index = currentSlideIndex(track) + direction;
    const slide = track.children[index];
    if (!slide) return;
    scrollTrackTo(track, slide, scrollBehavior());
  }

  #updateLightboxControls() {
    const dialog = this.#lightbox;
    const track = dialog?.querySelector('[data-gs-lb-track]');
    if (!dialog || !(track instanceof HTMLElement)) return;

    const index = currentSlideIndex(track);
    const counter = dialog.querySelector('[data-gs-lb-counter]');
    if (counter) counter.textContent = String(index + 1);

    const prev = dialog.querySelector('[data-gs-lb-prev]');
    const next = dialog.querySelector('[data-gs-lb-next]');
    prev?.setAttribute('aria-disabled', String(index === 0));
    next?.setAttribute('aria-disabled', String(index >= track.children.length - 1));
  }

  #onLightboxClose = () => {
    const dialog = this.#lightbox;
    if (!dialog) return;
    unlockScroll(dialog);

    const track = dialog.querySelector('[data-gs-lb-track]');
    const index = track instanceof HTMLElement ? currentSlideIndex(track) : 0;

    // On the swipe gallery, land on the image that was last viewed full screen and return focus
    // there; on the desktop grid, return focus to the tile that opened the lightbox.
    let target = this.#lightboxOpener;
    const gallery = this.querySelector('[data-gs-track]');
    const tile = gallery?.children[index];
    if (!DESKTOP_QUERY.matches && gallery instanceof HTMLElement && tile) {
      scrollTrackTo(gallery, tile, 'auto');
      const zoom = tile.querySelector('[data-gs-zoom]');
      if (zoom instanceof HTMLElement) target = zoom;
    }

    if (target?.isConnected) target.focus({ preventScroll: true });
    this.#lightboxOpener = null;
  };

  /* ---------------------------------------------------------------- Sticky bar (mobile) */

  #setupStickyBar() {
    const bar = this.querySelector('[data-gs-sticky]');
    const button = this.querySelector('[data-gs-main-add]');
    const end = this.querySelector('[data-gs-end]');
    if (!(bar instanceof HTMLElement) || !button || !('IntersectionObserver' in window)) return;

    this.#barObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
          if (entry.target === button) {
            this.#pastButton = !entry.isIntersecting && entry.boundingClientRect.top < rootTop;
          } else {
            this.#endReached = entry.isIntersecting || entry.boundingClientRect.top < rootTop;
          }
        }
        bar.classList.toggle('is-visible', this.#pastButton && !this.#endReached);
      },
      { rootMargin: '-56px 0px 0px 0px' }
    );

    this.#barObserver.observe(button);
    if (end) this.#barObserver.observe(end);
  }

  /* ---------------------------------------------------------------- Sticky panel (desktop) */

  /**
   * Keeps the information panel below the sticky header; when the panel is taller than the
   * window (open accordions), it sticks by its bottom edge instead so nothing is out of reach.
   * @param {AbortSignal} signal
   */
  #setupStickyPanel(signal) {
    const panel = this.querySelector('[data-gs-panel]');
    if (!(panel instanceof HTMLElement)) return;

    const update = () => {
      if (!DESKTOP_QUERY.matches) {
        panel.style.removeProperty('--gs-pdp-sticky-top');
        return;
      }

      const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
      const preferred = header + PANEL_GAP;
      const fitted = window.innerHeight - panel.offsetHeight - PANEL_GAP;
      panel.style.setProperty('--gs-pdp-sticky-top', `${Math.round(Math.min(preferred, fitted))}px`);
    };

    if ('ResizeObserver' in window) {
      this.#panelObserver = new ResizeObserver(() => update());
      this.#panelObserver.observe(panel);
    }

    window.addEventListener('resize', update, { passive: true, signal });
    window.addEventListener('load', update, { once: true, signal });
    DESKTOP_QUERY.addEventListener('change', update, { signal });
    update();
  }
}

if (!customElements.get('gs-product-page')) {
  customElements.define('gs-product-page', GsProductPage);
}
